<script setup>
/**
 * 一个场景 = 一个 Section：链编辑器 + 保存 + 被拒之后怎么办。
 *
 * 编辑缓冲区（`steps`）和库里的链（`props.mode.chain`）是两样东西：
 *   - 保存成功 → 父组件把返回的那一行换进列表 → 两者相等 → 「未保存」自动消失。
 *   - 保存被拒（409 / 400）→ 父组件不动那一行 → **编辑缓冲区原封不动**，错误条挂在区块里，旁边就是重试。
 *     把编辑好的链清掉再让人重编一遍，是对操作者最粗暴的对待。
 *
 * 「链不能空」这条不变量的权威在后端配置校验（事务里跑 `validateLlmConfig`，409 + 回滚）。前端不复述它，
 * 而是让它删不出来：`ChainEditor` 里只剩一步时删除键就是 `disabled`。
 *
 * `enabled` 原样带回去：这个区块只编辑链，不该顺手改掉别的列。
 */
import { computed, onMounted, ref } from 'vue'
import { Save } from '@lucide/vue'
import { Badge, Button, Section, useToast } from '@drama/ui'
import { useI18n } from 'vue-i18n'
import { saveLlmModeChain } from '../../../api/llm/index.js'
import { isChainDirty, toChainSaveBody, toEditableChain, validateChain } from '../mode-chain.js'
import ChainEditor from './ChainEditor.vue'

const props = defineProps({
  /** @type {import('vue').PropType<import('../../../api/llm/index.js').ModeRow>} */
  mode: { type: Object, required: true },
  /** @type {import('vue').PropType<import('../mode-chain.js').ChainStep[]>} 可选的 (厂商, 模型) 全集。 */
  options: { type: Array, required: true },
})

const emit = defineEmits(['saved'])

const { t } = useI18n()
const toast = useToast()

/** 编辑缓冲区。库里的链在 `props.mode.chain`，两者的差就是「未保存的改动」。 */
const steps = ref([])
const saving = ref(false)
const saveError = ref('')

onMounted(reset)

const validity = computed(() =>
  validateChain({ steps: steps.value, required: props.mode.required, options: props.options }),
)
const dirty = computed(() => isChainDirty(steps.value, props.mode.chain))
/** 有行没选完、或选了个已停用的 (厂商, 模型)：那不是配置，是没填完，不给存。 */
const hasStepError = computed(() => Object.keys(validity.value.steps).length > 0)

function reset() {
  steps.value = toEditableChain(props.mode.chain)
  saveError.value = ''
}

async function save() {
  saving.value = true
  saveError.value = ''
  try {
    const saved = await saveLlmModeChain(props.mode.mode, {
      ...toChainSaveBody(steps.value),
      enabled: props.mode.enabled,
    })
    toast.success(t('visualGameAdmin.llm.modes.toast.saved'))
    emit('saved', saved)
  } catch (err) {
    // 后端消息已经说清了是哪条不变量、改动是否回滚，原样显示，不改写成「保存失败」。
    saveError.value = err.message
    toast.error(t('visualGameAdmin.llm.modes.toast.failed'), { duration: 0 })
  } finally {
    saving.value = false
  }
}
</script>

<template>
  <Section
    :title="mode.mode"
    data-insp-path="apps/visual-game-admin/src/pages/llm/mode/ModeCard.vue#mode"
  >
    <template #actions>
      <Badge :variant="mode.required ? 'warning' : 'neutral'">
        {{ t(`visualGameAdmin.llm.modes.${mode.required ? 'required' : 'optional'}`) }}
      </Badge>
      <Badge :variant="mode.enabled ? 'success' : 'danger'">
        {{ t(`visualGameAdmin.llm.modes.status.${mode.enabled ? 'enabled' : 'disabled'}`) }}
      </Badge>
      <Badge
        v-if="dirty"
        variant="warning"
      >
        {{ t('visualGameAdmin.llm.modes.unsaved') }}
      </Badge>

      <Button
        variant="ghost"
        size="sm"
        :disabled="!dirty || saving"
        @click="reset"
      >
        {{ t('visualGameAdmin.llm.modes.actions.reset') }}
      </Button>
      <Button
        variant="primary"
        size="sm"
        :disabled="!dirty || hasStepError"
        :loading="saving"
        @click="save"
      >
        <Save :size="16" />
        {{ t('visualGameAdmin.llm.modes.actions.save') }}
      </Button>
    </template>

    <ChainEditor
      :steps="steps"
      :options="options"
      :step-errors="validity.steps"
      @update:steps="steps = $event"
    />

    <div
      v-if="saveError"
      class="mode__error"
      role="alert"
      data-insp-path="apps/visual-game-admin/src/pages/llm/mode/ModeCard.vue#save-error"
    >
      <p class="vga-error">
        {{ saveError }}
      </p>
      <Button
        variant="secondary"
        size="sm"
        :loading="saving"
        @click="save"
      >
        {{ t('visualGameAdmin.common.retry') }}
      </Button>
    </div>
  </Section>
</template>

<style scoped>
.mode__error {
  display: flex;
  align-items: center;
  gap: var(--drama-space-md);
  margin-top: var(--drama-space-lg);
  padding: var(--drama-space-md);
  border-radius: var(--drama-radius-md);
  background: color-mix(in srgb, var(--drama-color-danger) 10%, transparent);
  color: var(--drama-color-danger);
}

.mode__error p {
  /* min-width: 0 —— flex 项默认不缩到内容以下，后端那条长错误消息会被裁掉半句。 */
  flex: 1;
  min-width: 0;
  margin: 0;
}
</style>
